/**
 * Rounds the given value to the nearest multiple of `nearest`.
 * @example
 * roundToNearest(23, 10); // => 20
 * roundToNearest(13.45, 0.1); // => 13.5
 */
export function roundToNearest(value: number, nearest: number) {
  return Math.round(value / nearest) * nearest;
}

/**
 * Rounding function that resolves tie-breakers by rounding towards the nearest even number.
 * Also known as "gaussian rounding" or "banker's rounding".
 */
export function roundToEven(x: number) {
  return roundToNearest(x, 2);
}

/**
 * Rounds the value to the nearest pence value (nearest 0.01).
 */
export function roundToPence(x: number) {
  return roundToNearest(x, 0.01);
}

/**
 * True when the value is a usable number, including `0`.
 * Use this instead of a truthiness check when building query strings from identifiers
 * that can legitimately be `0` (agency numbers, FRN numbers, verification codes).
 * @example
 * isUsableNumber(0); // => true
 * isUsableNumber(NaN); // => false
 * isUsableNumber(undefined); // => false
 */
export function isUsableNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

/**
 * Parses a route/query param into a number, returning `undefined` rather than `NaN` when it is
 * missing or unparseable. Preserves `0`, which `Number(null)` and truthiness checks both mishandle.
 * @example
 * toOptionalNumber('0'); // => 0
 * toOptionalNumber(undefined); // => undefined
 * toOptionalNumber('abc'); // => undefined
 */
export function toOptionalNumber(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isNaN(parsed) ? undefined : parsed;
}
